/**
 * Hegic's break-in deployment, normalised.
 *
 * A separate backend from claims — hegicb.api.camcom.ai, and source_name
 * `hdfcergo` where claims uses `hdfcergo_pp` — but the same contracts, which
 * is what makes this small. All three shapes were probed live before any of
 * this was written; the fixtures beside it are those responses.
 *
 * Pure: no axios, no React, no clock.
 */

const asRows = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") return Object.values(raw);
  return [];
};

const count = (value) => {
  // Every figure in /assessment_report arrives as a STRING ("592995").
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

/**
 * The headline figures.
 *
 * `total_claims` is the backend's own total and is NOT the sum of the others —
 * completed + review + failed + updated overshoots it, because a claim can be
 * both updated and completed. So the parts are never presented as a
 * breakdown of the whole, and no percentage is derived from them beyond the
 * two the numbers genuinely support.
 */
export const toBreakinReport = (raw) => {
  const total = count(raw?.total_claims);
  const completed = count(raw?.completed);
  const failed = count(raw?.failed);
  const review = count(raw?.review);
  const updated = count(raw?.updated);
  return {
    total,
    completed,
    failed,
    review,
    updated,
    // Out of the total the backend states, which is the only denominator it
    // gives us.
    completedRate: total > 0 ? completed / total : null,
    failedRate: total > 0 ? failed / total : null,
  };
};

/**
 * One page of break-in claims.
 *
 * Identical envelope to Hegic's claims list: `data` is a dict keyed "0","1",…
 * and paging is by OFFSET, not page number — `next: {limit, offset}` with
 * `previous: -1` when there is none.
 */
export const toBreakinCases = (raw) => {
  const rows = asRows(raw?.data).filter((row) => row && typeof row === "object");
  const next = raw?.next;
  return {
    rows: rows.map((row) => ({
      id: String(row.id ?? row.ref_num ?? ""),
      refNum: typeof row.ref_num === "string" ? row.ref_num : String(row.ref_num ?? ""),
      claimState: row.claim_state == null ? null : String(row.claim_state),
      // claim_state2 tracks the review side and differs from claim_state on
      // rows mid-review; both are kept rather than one being guessed at.
      reviewState: row.claim_state2 == null ? null : String(row.claim_state2),
      createdAt: typeof row.created_at === "string" ? row.created_at : null,
    })),
    total: count(raw?.total_countOfClaims),
    nextOffset:
      next && Number.isFinite(Number(next.offset)) ? Number(next.offset) : null,
    limit: next && Number.isFinite(Number(next.limit)) ? Number(next.limit) : null,
  };
};

/**
 * Who reviewed how much, busiest first.
 *
 * `count` is reviews inside the requested window, so it is zero for everyone
 * on a quiet range — which is a real answer, not an empty state, and the page
 * says so rather than showing an empty table.
 */
export const toReviewers = (raw) => {
  const rows = asRows(raw)
    .filter((row) => row && typeof row === "object" && (row.email_id || row.username))
    .map((row) => ({
      email: typeof row.email_id === "string" ? row.email_id : "",
      name: typeof row.username === "string" ? row.username : "",
      role: typeof row.role === "string" ? row.role : "",
      count: count(row.count),
    }));

  const reviewed = rows.reduce((sum, row) => sum + row.count, 0);
  return {
    rows: rows.sort(
      (a, b) => b.count - a.count || (a.name || a.email).localeCompare(b.name || b.email),
    ),
    people: rows.length,
    reviewed,
    active: rows.filter((row) => row.count > 0).length,
    roles: [...new Set(rows.map((row) => row.role).filter(Boolean))].sort(),
  };
};

/**
 * Break-in's own claim_state vocabulary.
 *
 * The codes are shared with claims but the MEANINGS are not: 3 is Completed
 * here where claims calls it AI Assessed, and 8 is Accepted where claims says
 * Review Completed. Rendering a break-in row through the claims labels
 * produces a sentence that is wrong and looks right, which is the worst kind.
 *
 * Recorded in the route's own notes before this page existed; passed to
 * getStatusInfoFor, which overrides the wording and keeps the colour and icon
 * that already suit each state.
 */
export const BREAKIN_STATUS_LABELS = Object.freeze({
  0: "Initiated",
  1: "In process",
  3: "Completed",
  6: "Failed",
  8: "Accepted",
});
